import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Flex, Text, Icon, useColorModeValue, Button } from '@chakra-ui/react'
import { DataTable } from '@/components/DataTable'
import { ArrowLeftIcon } from '@/components/icons/ArrowLeftIcon'

import { formatInvoiceDate } from '@/utils/date'
import { formatPrice } from '@/utils/formatPrice'
import { useMemo } from 'react'

import { IBillingHistory } from '@/types/billing.interface'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

type CustomColumnProps = {
  title?: string
}

type CustomColumnDef = CustomColumnProps &
  ColumnDef<IBillingHistory, string | number>

interface BillingHistoryTableProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
  isDrawer?: boolean
}

export const BillingHistoryTable = ({
  data,
  isDrawer = false,
}: BillingHistoryTableProps) => {
  const t = useTranslations()
  const minersColumnsHelper = createColumnHelper<IBillingHistory>()
  const tableBg = useColorModeValue('white', 'primaryElevation')
  const tableBorderColor = useColorModeValue('blackAlpha.200', '#434445')
  const blackOrWhite = useColorModeValue('black', 'white')
  const buttonHover = useColorModeValue('blue.500', 'blue.500')

  const minersColumns = useMemo<CustomColumnDef[]>(() => {
    const cols: (CustomColumnDef | null)[] = [
      Object.assign(
        minersColumnsHelper.accessor('invoiceMonth', {
          id: 'invoiceMonth',
          header: () => (
            <Flex>
              <Text color={blackOrWhite}>{t('common.month')}</Text>
            </Flex>
          ),
          cell: ({ row }) => (
            <Text>
              {formatInvoiceDate(
                row.original.invoiceMonth,
                row.original.invoiceYear
              )}
            </Text>
          ),
        }),
        { title: t('common.month') }
      ) as CustomColumnDef,

      // ❗ only add when NOT drawer
      !isDrawer
        ? (Object.assign(
            minersColumnsHelper.accessor('minersInvoiced', {
              id: 'minersInvoiced',
              header: () => (
                <Flex>
                  <Text color={blackOrWhite}>
                    {t(
                      'dashboard.billing.billing-history-details.miners-invoiced'
                    )}
                  </Text>
                </Flex>
              ),
              cell: ({ row }) => (
                <Text>{row.original.minersInvoiced.toLocaleString()}</Text>
              ),
            }),
            {
              title: t(
                'dashboard.billing.billing-history-details.miners-invoiced'
              ),
            }
          ) as CustomColumnDef)
        : null,

      Object.assign(
        minersColumnsHelper.accessor('avgUptimePercentage', {
          id: 'avgUptimePercentage',
          header: () => (
            <Flex>
              <Text color={blackOrWhite}>
                {t('dashboard.billing.billing-history-details.uptime-avg')}
              </Text>
            </Flex>
          ),
          cell: ({ row }) => (
            <Text>
              {`${Math.round(Number(row.original.avgUptimePercentage))}%`}
            </Text>
          ),
        }),
        { title: t('dashboard.billing.billing-history-details.uptime-avg') }
      ) as CustomColumnDef,

      Object.assign(
        minersColumnsHelper.accessor('totalMonthlyHostingFee', {
          id: 'totalMonthlyHostingFee',
          header: () => (
            <Flex>
              <Text color={blackOrWhite}>
                {t(
                  isDrawer
                    ? 'dashboard.miners.hosting-paid'
                    : 'dashboard.billing.billing-history-details.billed'
                )}
              </Text>
            </Flex>
          ),
          cell: ({ row }) => (
            <Text>{formatPrice(row.original.totalMonthlyHostingFee)}</Text>
          ),
        }),
        { title: t('dashboard.billing.billing-history-details.billed') }
      ) as CustomColumnDef,

      Object.assign(
        minersColumnsHelper.accessor('downtimeCredits', {
          id: 'downtimeCredits',
          header: () => (
            <Flex>
              <Text color={blackOrWhite}>{t('dashboard.billing.credits')}</Text>
              <Text
                display={isDrawer ? 'none' : 'block'}
                color="gray.400"
                as="span"
              >
                *
              </Text>
            </Flex>
          ),
          cell: ({ row }) => (
            <Text>{formatPrice(row.original.downtimeCredits)}</Text>
          ),
        }),
        { title: t('dashboard.billing.credits') }
      ) as CustomColumnDef,

      Object.assign(
        minersColumnsHelper.display({
          id: 'monthlyDetail',
          header: ({}) => <></>,
          meta: {
            additionalTdClass: 'mobile-full-width',
          },
          cell: ({ row }) => {
            return (
              <Flex justify={{ base: 'center', md: 'flex-end' }} w="full">
                <Link
                  href={`/dashboard/billing/history/uptime/${row.original.invoiceYear}/${row.original.invoiceMonth}`}
                  passHref
                  style={{
                    textDecoration: 'none',
                    width: '100%',
                  }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    w={{ base: 'full', md: 'auto' }}
                    color={blackOrWhite}
                    _hover={{
                      color: buttonHover,
                      borderColor: buttonHover,
                    }}
                    rightIcon={
                      <Icon as={ArrowLeftIcon} transform="rotate(180deg)" />
                    }
                  >
                    {t('common.labels.details')}
                  </Button>
                </Link>
              </Flex>
            )
          },
        }),
        { title: '' }
      ) as CustomColumnDef,
    ]

    // remove nulls (when isDrawer is true)
    return cols.filter((c): c is CustomColumnDef => Boolean(c))
  }, [minersColumnsHelper, isDrawer, t, blackOrWhite, buttonHover])

  return (
    <DataTable
      className="table-responsive billing-history-mobile"
      data={data}
      columns={minersColumns}
      borderRadius={isDrawer ? '8px' : undefined}
      bg={isDrawer ? tableBg : undefined}
      dividerColor={isDrawer ? tableBorderColor : undefined}
      borderWidth={isDrawer ? 1 : 0}
      borderColor={isDrawer ? tableBorderColor : undefined}
      removeLastLine
      headerBorderBottom="1px solid #CBD5E0"
    />
  )
}
